import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useToast } from '@/context/ToastContext';
import SpinnerOverlay from '@/common/SpinnerOverlay/SpinnerOverlay';
import { getSerialMstrsByLaserFilter, getSerialMstr, getLatestSerialMstrCt } from '@/api/endpoints';
import { SerialMstr as ModelSerialMstr, SerialMstrDetails, CTData, SerialMstr } from '@/api/models';
import { debounce } from '@/utils/debounce';
import styles from './SerialNumberAutocomplete.module.scss';

const MIN_CHARACTERS = 5;

interface SerialNumberData {
  serialNumber: string;
  details: SerialMstrDetails;
  ctData: CTData;
}

interface SerialNumberAutocompleteProps {
  value: string;
  /* eslint-disable no-unused-vars */
  onChange: (value: string) => void;
  onSelect?: (data: SerialNumberData) => void;
  /* eslint-enable no-unused-vars */
  className?: string;
  label?: string;
  placeholder?: string;
  variant?: 'normal' | 'large' | 'extra' | 'xxl' | 'fullWidth';
}

const SerialNumberAutocomplete: React.FC<SerialNumberAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  className = '',
  label = 'Serial Number',
  placeholder = `Type ${MIN_CHARACTERS}+ characters`,
  variant = 'normal',
}) => {
  const [searchTerm, setSearchTerm] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ModelSerialMstr[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();

  // Search function
  const searchSerialNumbers = useCallback(
    async (term: string) => {
      if (term.length < MIN_CHARACTERS) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);

      try {
        const response = await getSerialMstrsByLaserFilter({
          filter: term,
          currentPage: 0,
          pageSize: 100, // Increased page size to show more results
          pagesToGet: 1,
        });

        setSuggestions(
          response.data.map((item) => ({
            ...item,
            serialMstrId: item.serialMstrId.toString(),
          })),
        );
      } catch (err) {
        console.error(`SerialNumberAutocomplete.searchSerialNumbers: ${err}`);
        showToast({
          message: 'Failed to search serial numbers',
          status: 'error',
          showCheckmark: false,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [showToast],
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      searchSerialNumbers(term);
    }, 300),
    [searchSerialNumbers],
  );

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    onChange(value);
    debouncedSearch(value);
    setIsDropdownOpen(true);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle serial number selection
  const handleSelect = async (serial: SerialMstr) => {
    setIsLoading(true);
    const newValue = serial.laserNo;
    setSearchTerm(newValue);
    onChange(newValue);
    setSuggestions([]);
    setIsDropdownOpen(false);

    try {
      // Make parallel API calls
      const [details, ctData] = await Promise.all([getSerialMstr(serial.serialMstrId), getLatestSerialMstrCt(serial.serialMstrId)]);

      onSelect?.({
        serialNumber: newValue,
        details,
        ctData,
      });
    } catch (err) {
      console.error(`SerialNumberAutocomplete.handleSelect: ${err}`);
      showToast({
        message: 'Failed to load serial number details',
        status: 'error',
        showCheckmark: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Combine classes for the container
  const containerClasses = [styles.container, variant ? styles[variant] : '', className].filter(Boolean).join(' ');

  // Input classes
  const inputClasses = [
    styles.input,
    'centerAlign', // This will use the centerAlign class from the stylesheet
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses} ref={wrapperRef}>
      {label && <div className={styles.label}>{label}</div>}
      <div className={styles.inputContainer}>
        <input type="text" value={searchTerm} onChange={handleChange} onFocus={() => setIsDropdownOpen(true)} placeholder={placeholder} className={inputClasses} autoComplete="off" />
        {isDropdownOpen && suggestions.length > 0 && (
          <div className={styles.dropdown} style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {suggestions.map((suggestion) => (
              <div key={suggestion.serialMstrId} onClick={() => handleSelect(suggestion)} className={styles.dropdownItem}>
                {suggestion.laserNo}
              </div>
            ))}
            {isLoading && <div className={styles.loading}>Loading...</div>}
          </div>
        )}
      </div>
      {isLoading && <SpinnerOverlay label="Loading" />}
    </div>
  );
};

export default SerialNumberAutocomplete;
